import { useState } from 'react';
import { Icons } from '../Icons';
import { exportTaxReport, COUNTRY_NAMES } from '../../services/taxExport';
import { useToast } from '../Toast';
import type { Token, Prices, FIFOResults } from '../../types';
import './TaxExportModal.css';

interface TaxExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  tokens: Token[];
  prices: Prices;
  fifoResults: FIFOResults;
}

export function TaxExportModal({
  isOpen,
  onClose,
  tokens,
  prices,
  fifoResults,
}: TaxExportModalProps) {
  const toast = useToast();
  const [country, setCountry] = useState<'FR' | 'US' | 'UK' | 'DE'>('FR');
  const [year, setYear] = useState<number>(new Date().getFullYear());

  if (!isOpen) return null;

  const handleExport = () => {
    try {
      exportTaxReport(tokens, prices, fifoResults, country, year);
      onClose();
    } catch (error) {
      console.error('Tax export error:', error);
      toast.error('Erreur lors de l\'export fiscal. Veuillez réessayer.');
    }
  };

  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear - 1, currentYear - 2];

  return (
    <div className="tax-export-overlay" onClick={onClose}>
      <div className="tax-export-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="tax-export-header">
          <div className="tax-export-header-left">
            <div className="tax-export-icon">📄</div>
            <h2 className="tax-export-title">Export Rapport Fiscal</h2>
          </div>
          <button
            className="tax-export-close"
            onClick={onClose}
            aria-label="Fermer"
          >
            <Icons.X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="tax-export-content">
          {/* Country selection */}
          <div className="tax-export-field">
            <label htmlFor="country-select" className="tax-export-label">
              Pays
            </label>
            <select
              id="country-select"
              className="tax-export-select"
              value={country}
              onChange={(e) => setCountry(e.target.value as 'FR' | 'US' | 'UK' | 'DE')}
            >
              {Object.entries(COUNTRY_NAMES).map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          {/* Year selection */}
          <div className="tax-export-field">
            <label htmlFor="year-select" className="tax-export-label">
              Année
            </label>
            <select
              id="year-select"
              className="tax-export-select"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          {/* Warning alert */}
          <div className="tax-export-alert">
            <div className="tax-export-alert-icon">⚠️</div>
            <div className="tax-export-alert-content">
              <strong>Important:</strong> Ce rapport est fourni à titre informatif
              uniquement. Consultez un expert-comptable pour votre déclaration
              fiscale officielle.
            </div>
          </div>

          {/* Info about the report */}
          <div className="tax-export-info">
            <h3 className="tax-export-info-title">Contenu du rapport:</h3>
            <ul className="tax-export-info-list">
              <li>📊 Toutes transactions {year} (achats, ventes, swaps, mining, staking)</li>
              <li>💰 Calcul des plus-values et moins-values (achats/ventes)</li>
              <li>⏱️ Gains court-terme (&lt;1 an) vs long-terme (&gt;1 an)</li>
              <li>💸 Estimation de l'impôt selon votre pays</li>
              <li>✨ Traçabilité complète des swaps avec cost basis chain</li>
              <li>⛏️ Traçabilité mining (pool, hashrate)</li>
              <li>🔒 Traçabilité staking (platform, APY, duration)</li>
              <li>📁 Format CSV compatible Excel</li>
            </ul>
            <p style={{ marginTop: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
              <strong>📋 Note:</strong> Les transactions Swap, Mining et Staking sont <strong>incluses pour traçabilité</strong> mais ne sont pas encore calculées dans les totaux fiscaux. Ces données sont préparées pour la future conformité MiCA.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="tax-export-footer">
          <button
            className="tax-export-button secondary"
            onClick={onClose}
          >
            Annuler
          </button>
          <button
            className="tax-export-button primary"
            onClick={handleExport}
          >
            <Icons.Download size={18} />
            Générer Rapport Fiscal CSV
          </button>
        </div>
      </div>
    </div>
  );
}
